import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Skeleton, Textarea } from "@playbookai/ui";
import {
  discoveryBlockedReason,
  type PolicyDiscoveryState,
} from "./usePolicyDiscovery.js";

export interface AllowlistOption {
  readonly id: string;
  readonly label: string;
}

export interface PolicyAllowlistPickerProps {
  readonly id: string;
  readonly label: string;
  readonly discovery: PolicyDiscoveryState;
  /** Fetches the options this allowlist chooses from, over the connection. */
  readonly load: (connectionId: string) => Promise<readonly AllowlistOption[]>;
  /** Cache key beyond the connection — resource type, or capability + field. */
  readonly queryKey: readonly unknown[];
  /**
   * Currently allowed ids. `undefined` is "no restriction"; `[]` is "nothing
   * is permitted" — the policy model's own distinction, which this control has
   * to surface rather than collapse.
   */
  readonly value: readonly string[] | undefined;
  /** `undefined` removes the restriction entirely. */
  readonly onChange: (next: readonly string[] | undefined) => void;
  readonly disabled?: boolean;
}

/**
 * PB-4734 (G9) — choose an allowlist from what the provider actually has.
 *
 * The free-text textarea this replaces validated the FORMAT of an id and never
 * its existence, so the common failure was silent: a typo produced a policy
 * that looked like a restriction and matched nothing, and the first sign was a
 * user being refused an action nobody had meant to forbid.
 *
 * ## Direct entry stays
 *
 * Not as a fallback for when the picker breaks — as a first-class route.
 * Automation and recovery must not require a browser, the policy API keeps
 * accepting ids directly, and an administrator restoring a configuration should
 * not have to click twenty checkboxes. The toggle is deliberately not hidden
 * behind a failure.
 *
 * ## Absent and empty mean opposite things, and the UI has to say so
 *
 * No entry is "no restriction on this"; an empty list is "nothing is
 * permitted". Both are legal and they are opposite, so unticking the last
 * value keeps the restriction — as deny-all, the fail-closed reading — and
 * removing the restriction is its own explicit control. The caption states
 * which of the three states the policy is in.
 */
export function PolicyAllowlistPicker({
  id,
  label,
  discovery,
  load,
  queryKey,
  value,
  onChange,
  disabled,
}: PolicyAllowlistPickerProps): ReactElement {
  const [manual, setManual] = useState(false);

  /* The raw text while the direct-entry box is being edited, or null when it
     should mirror `ids`.

     The box used to be controlled straight off the parsed result — its value
     was `ids.join("\n")` and every keystroke re-parsed, trimmed and dropped
     empty entries. So a separator was deleted the instant it was typed: with
     `["a"]`, typing "," re-rendered as "a", and the following "b" produced
     `["ab"]`. A SECOND ID COULD NOT BE TYPED AT ALL, which is the one thing
     this control is for.

     Holding the raw text means separators and trailing spaces survive long
     enough to type the next entry. `onChange` still publishes the parsed
     entries, so nothing downstream sees the half-finished text. */
  const [manualText, setManualText] = useState<string | null>(null);
  const restricted = value !== undefined;
  const ids = value ?? [];

  const options = useQuery({
    /* The CONNECTION is part of what identifies this result: options are read
       over it, and two connections do not see the same things. Without it a
       reconnect — or a credential resolving to a different connection — serves
       the previous connection's options from cache, and an administrator
       allowlists ids the current one cannot reach. That is the silent
       under-allow this control exists to prevent. */
    queryKey: [
      "policy-allowlist",
      discovery.kind === "ready" ? discovery.connectionId : null,
      ...queryKey,
    ],
    queryFn: () =>
      load(discovery.kind === "ready" ? discovery.connectionId : ""),
    // Also gated on NOT being disabled. A disabled picker is one whose
    // product is switched off, and listing for it spends a provider round trip
    // (and a rate-limit token) on options nobody can select.
    enabled: discovery.kind === "ready" && !manual && disabled !== true,
    // A provider list is not worth retrying into: the interesting failures here
    // are authorization ones, and repeating them just delays the message.
    retry: false,
  });

  const toggle = (optionId: string): void => {
    // Unticking the LAST value yields [], not undefined: the administrator is
    // narrowing a restriction, and the fail-closed end of that act is "none",
    // never "everything". Removal has its own button above.
    onChange(
      ids.includes(optionId)
        ? ids.filter((v) => v !== optionId)
        : [...ids, optionId],
    );
  };

  return (
    // `role="group"` + `aria-labelledby` rather than relying on the label's
    // `htmlFor`: in a BLOCKED state there is no form control to point at, and a
    // label whose target does not exist is worse than none — assistive
    // technology announces nothing, and the reason the picker is empty is
    // exactly what a blocked user needs to hear.
    <div className="space-y-1.5" role="group" aria-labelledby={`${id}-label`}>
      <div className="flex items-center justify-between gap-2">
        <label
          id={`${id}-label`}
          htmlFor={id}
          className="block text-xs font-medium"
        >
          {label}
        </label>
        <div className="flex items-center gap-1">
          {restricted && disabled !== true ? (
            <Button
              size="sm"
              variant="ghost"
              type="button"
              onClick={() => onChange(undefined)}
            >
              Remove restriction
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => {
              // Switching modes discards the half-typed buffer: the picker and
              // the text box must not disagree about what is allowlisted.
              setManualText(null);
              setManual((m) => !m);
            }}
          >
            {manual ? "Choose from list" : "Enter ids directly"}
          </Button>
        </div>
      </div>

      {manual ? (
        <Textarea
          id={id}
          rows={2}
          disabled={disabled === true}
          value={manualText ?? ids.join("\n")}
          placeholder="Leave blank for no restriction"
          onChange={(event) => {
            const raw = event.target.value;
            // Keep the raw text so the separator the user just typed is still
            // there on the next render.
            setManualText(raw);
            const entries = raw
              .split(/[\n,]/)
              .map((entry) => entry.trim())
              .filter((entry) => entry !== "");
            // Blank here is REMOVAL — the text route cannot distinguish "permit
            // nothing" from "stop restricting", and clearing a field reads as
            // removal everywhere else. Deny-all is authored in the picker.
            onChange(entries.length === 0 ? undefined : entries);
          }}
          /* Hand the box back to `ids` on the way out, so it re-renders
             normalised — one entry per line, no stray separators — and later
             changes from elsewhere are reflected. */
          onBlur={() => setManualText(null)}
        />
      ) : (
        <PickerBody
          id={id}
          discovery={discovery}
          options={options}
          value={ids}
          disabled={disabled === true}
          onToggle={toggle}
        />
      )}

      <p className="text-xs text-pb-text-muted">
        {!restricted
          ? "No restriction. Everything this integration can reach is allowed."
          : ids.length === 0
            ? "Nothing is permitted. Every value is refused until one is selected — or remove the restriction."
            : `${ids.length} allowed. Anything not selected is refused, including if someone sends its id directly.`}
      </p>
    </div>
  );
}

function PickerBody({
  id,
  discovery,
  options,
  value,
  disabled,
  onToggle,
}: {
  id: string;
  discovery: PolicyDiscoveryState;
  options: {
    isPending: boolean;
    error: unknown;
    data: readonly AllowlistOption[] | undefined;
  };
  value: readonly string[];
  disabled: boolean;
  onToggle: (optionId: string) => void;
}): ReactElement {
  // THE BLOCKED STATES, each naming its own cause and next step. An empty
  // dropdown would say "this tenant has no projects", which is a different and
  // far more alarming claim than "you have not connected yet".
  if (discovery.kind === "loading") return <Skeleton className="h-16 w-full" />;

  if (discovery.kind === "unavailable") {
    return (
      <Alert tone="watch" title="Could not check your connections">
        We could not read your connections, so there is nothing to list from
        yet. Reload to try again, or enter ids directly.
      </Alert>
    );
  }

  if (discovery.kind === "not-connected") {
    return (
      <Alert tone="info" title="Connect your account to choose from a list">
        Listing runs over your own connection — there is no workspace-wide
        credential to read the provider with. Grant yourself access, connect
        under Your connections, then come back. You can enter ids directly in
        the meantime.
      </Alert>
    );
  }

  if (discovery.kind === "connection-unusable") {
    return (
      <Alert tone="watch" title="Your connection is not usable">
        It is {discovery.status}. Reconnect it under Your connections, or enter
        ids directly.
      </Alert>
    );
  }

  // DISABLED comes before the pending check, and has to. A disabled picker does
  // not run its query, so TanStack keeps reporting `isPending` forever — the
  // skeleton below would spin for the life of the page on every control whose
  // product is switched off. Render what is already selected instead, so an
  // administrator can still SEE the stored policy of a product they have turned
  // off without turning it back on.
  if (disabled) {
    // The query is gated off while disabled, so `options.data` is usually
    // undefined here — and rendering it as the list showed an EMPTY box under a
    // caption still claiming "N allowed". The stored ids themselves are the
    // honest fallback: no labels without the provider, but the policy stays
    // visible, which is what this branch exists for.
    const shown =
      options.data ??
      value.map((storedId) => ({ id: storedId, label: storedId }));
    return (
      <OptionList
        id={id}
        options={shown}
        value={value}
        disabled
        onToggle={onToggle}
      />
    );
  }

  if (options.isPending) return <Skeleton className="h-16 w-full" />;

  if (options.error !== null && options.error !== undefined) {
    return (
      <Alert tone="watch" title="Could not list from the provider">
        {discoveryBlockedReason(options.error) ??
          "The provider did not answer. You can enter ids directly."}
      </Alert>
    );
  }

  const available = options.data ?? [];
  if (available.length === 0) {
    return (
      <Alert tone="info" title="Nothing to choose from">
        Your account can see nothing of this kind on the provider. Anything you
        allowlist here must be something you can see, so enter ids directly if
        you know them.
      </Alert>
    );
  }

  return (
    <OptionList
      id={id}
      options={available}
      value={value}
      disabled={disabled}
      onToggle={onToggle}
    />
  );
}

/** The checkbox list, shared by the live and disabled branches. */
function OptionList({
  id,
  options,
  value,
  disabled,
  onToggle,
}: {
  id: string;
  options: readonly AllowlistOption[];
  value: readonly string[];
  disabled: boolean;
  onToggle: (optionId: string) => void;
}): ReactElement {
  return (
    <div id={id} className="space-y-1 rounded-md border border-pb-border p-2">
      {options.map((option) => (
        <Checkbox
          key={option.id}
          checked={value.includes(option.id)}
          disabled={disabled}
          onChange={() => onToggle(option.id)}
          label={option.label}
        />
      ))}
    </div>
  );
}
